import { Injectable } from '@angular/core';
import { MicrophoneAccessError } from './microphone-access-error';

export type MicrophonePermissionStatus =
  | 'granted'
  | 'denied'
  | 'prompt'
  | 'unknown';

@Injectable()
export class MicrophoneAccessService {
  /**
   * Checks the current microphone permission status using the Permissions API.
   * Falls back to 'unknown' if the API is not available.
   */
  private async checkPermission(): Promise<MicrophonePermissionStatus> {
    try {
      if (navigator.permissions?.query) {
        const result = await navigator.permissions.query({
          name: 'microphone' as PermissionName,
        });
        return result.state as MicrophonePermissionStatus;
      }
    } catch (error) {
      // Permission API not supported or failed, will determine from getUserMedia
      console.warn(
        'Could not check microphone permission:',
        error instanceof Error ? error : String(error),
      );
    }
    return 'unknown';
  }

  /**
   * Determines if an error is due to microphone permission denial.
   */
  private isPermissionDeniedError(error: unknown): boolean {
    if (!error || typeof error !== 'object') {
      return false;
    }

    const errorName = 'name' in error ? (error.name as string) : '';
    if (
      errorName === 'NotAllowedError' ||
      errorName === 'PermissionDeniedError'
    ) {
      return true;
    }

    const errorMessage =
      'message' in error && typeof error.message === 'string'
        ? error.message.toLowerCase()
        : '';
    return (
      errorMessage.includes('permission') || errorMessage.includes('denied')
    );
  }

  /**
   * Requests microphone access with permission tracking and error handling.
   *
   * Flow:
   * 1. Checks current permission status via Permissions API (if available)
   * 2. Requests MediaStream via `getUserMedia()`
   * 3. On failure, throws `MicrophoneAccessError` with permission status
   *
   * @returns Promise resolving to the MediaStream on success
   * @throws MicrophoneAccessError if microphone access fails (status: `'denied'`, `'prompt'`, or `'unknown'`)
   */
  public async requestMediaStream(): Promise<MediaStream> {
    // Check microphone permission status before requesting
    const permissionStatus = await this.checkPermission();

    try {
      // Microphone access granted
      return await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
    } catch (error) {
      // Determine if error is due to permission denial
      const isPermissionDenied = this.isPermissionDeniedError(error);

      const finalStatus: MicrophonePermissionStatus = isPermissionDenied
        ? 'denied'
        : permissionStatus;

      // Throw specific error type for centralized handling in caller
      // Analytics tracking will be done in transitionToError() in the main service
      throw new MicrophoneAccessError(
        'Failed to access microphone',
        error,
        finalStatus,
      );
    }
  }
}
